// STAPLL — lazy autoplay video (assets/vi-lazy-video.js)
// Behaviour for snippets/vi-lazy-video.liquid: attach the file ~200px before a video scrolls into view,
// play it while visible and pause it when it leaves. Impact's <video-media autoplay> also calls play()
// on entering the viewport, but only once its own observer fires and it never pauses; this file adds
// the lazy source, the pause, and a play after the source is attached (Impact's call can come first and
// fail on a video that has no source yet).

const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const PAUSE_ICON = 'M6 5h4v14H6zM14 5h4v14h-4z';
const PLAY_ICON = 'M8 5v14l11-7z';

function play(video) {
  const host = video.closest('video-media');
  if (host && typeof host.play === 'function') host.play();
  else video.play().catch(() => {});
}

function pause(video) {
  const host = video.closest('video-media');
  if (host && typeof host.pause === 'function') host.pause();
  else video.pause();
}

function attach(video) {
  if (video.src) return;
  video.preload = 'metadata';
  if (video.dataset.viLazyFallback) {
    // the smaller rendition could not be loaded: use the file Shopify lists
    video.addEventListener('error', () => {
      video.src = video.dataset.viLazyFallback;
      if (video.viLazyVisible && !reduced) play(video);
    }, { once: true });
  }
  video.src = video.dataset.viLazySrc;
}

const near = new IntersectionObserver((entries) => {
  entries.forEach((entry) => {
    if (!entry.isIntersecting) return;
    attach(entry.target);
    near.unobserve(entry.target);
  });
}, { rootMargin: '200px' });

// The poster is set well ahead of the video file, so it is there when the video comes into view. A poster
// attribute in the markup would download at once, also for the hidden copy below.
const nearPoster = new IntersectionObserver((entries) => {
  entries.forEach((entry) => {
    if (!entry.isIntersecting) return;
    const video = entry.target;
    if (!video.poster) video.poster = video.dataset.viLazyPoster;
    nearPoster.unobserve(video);
  });
}, { rootMargin: '800px' });

// Hidden copies (Impact renders a phone and a desktop video, one display:none) never intersect.
const view = new IntersectionObserver((entries) => {
  entries.forEach((entry) => {
    const video = entry.target;
    video.viLazyVisible = entry.isIntersecting;
    if (entry.isIntersecting) {
      attach(video);
      if (!video.viLazyUserPaused) play(video);
    } else {
      pause(video);
    }
  });
});

// Reduced motion: no autoplay. Impact's <video-media autoplay> still calls play() when it scrolls into
// view (and its CSS only shows the video in autoplay mode), so keep the attribute, show native controls,
// and undo any playback the visitor did not start. A video marked data-vi-lazy-no-controls (inside a link)
// gets no controls, so the visitor cannot start it: it always stays on its poster.
function holdForVisitor(video) {
  const noControls = video.hasAttribute('data-vi-lazy-no-controls');
  let started = false;
  const start = () => { started = true; };
  video.addEventListener('pointerdown', start);
  video.addEventListener('keydown', start);
  video.addEventListener('play', () => {
    const active = navigator.userActivation ? navigator.userActivation.isActive : false;
    if (noControls || (!started && !active)) video.pause();
  });
  if (!noControls) video.controls = true;
}

// Pause / play button (WCAG 2.2.2): the visitor's choice wins over autoplay until they press it again.
function setUpToggle(video) {
  const toggle = video.parentElement && video.parentElement.querySelector('[data-vi-lazy-toggle]');
  if (!toggle) return;
  const icon = toggle.querySelector('path');
  const render = () => {
    const paused = !!video.viLazyUserPaused;
    toggle.setAttribute('aria-label', paused ? 'Play video' : 'Pause video');
    icon.setAttribute('d', paused ? PLAY_ICON : PAUSE_ICON);
  };
  toggle.addEventListener('click', () => {
    video.viLazyUserPaused = !video.viLazyUserPaused;
    if (video.viLazyUserPaused) pause(video);
    else { attach(video); play(video); }
    render();
  });
  render();
  toggle.hidden = false;
}

function scan() {
  document.querySelectorAll('video[data-vi-lazy-src]:not([data-vi-lazy-init])').forEach((video) => {
    video.setAttribute('data-vi-lazy-init', '');
    near.observe(video);
    if (video.dataset.viLazyPoster) nearPoster.observe(video);
    if (reduced) holdForVisitor(video);
    else { view.observe(video); setUpToggle(video); }
  });
}

// Wait for Impact's <video-media> so play()/pause() go through it (it tracks the `playing` state).
customElements.whenDefined('video-media').then(scan);
// Theme editor: a re-rendered section brings new <video> elements.
document.addEventListener('shopify:section:load', scan);
